import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { StateService } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { SpellingPagesDialogComponent } from '@client/app/pages/qa/spellcheck/pages/spelling-pages-dialog.component';
import { SpellingPlayComponent } from '@client/app/pages/qa/spellcheck/play/spelling-play.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'spelling-errors-index',
    standalone: false,
    template: `
        <page-header
                icon="{{['QA', 'MISSPELLINGS'] | monIcons}}"
                header="{{ headline }}"
                subHeader="{{subheaderTranslation}}">
        </page-header>
        <mon-tabs
                [options]="tabs"
                [(status)]="activeTab"
                (onStatus)="onStatus($event)"
                (onSearch)="onSearch($event)"
                [search]="search"
                [minSearchLength]="1"
                [showSearch]="true"
                [showFiltersBtn]="true"
                [(isFiltersOpen)]="isFiltersOpen">
            <div class="pull-left outer-r-small btn btn-border-radius btn-secondary inner-t-small inner-b-small inner-r-medium inner-l-medium"
                 (click)="playMisspelling()"
                 *ngIf="showPlay && ('misspelling_play' | activeFeature)"
                 monEventTrigger="actionTrigger"
                 [monEventParams]="{action:'qa-misspelling-play'}"
                 role="button"
                 [attr.aria-label]="'Play misspelling' | translate">
                {{'Play' | translate}}
            </div>
            <csv-export-button category="{{ type }}_misspelling"></csv-export-button>
        </mon-tabs>
        <div class="card panel-monsido-card">
            <div class="card-body">
                <div class="px-4">
                    <table-container-top>
                        <div class="outer-b merge-r-36 merge-l-36 merge-t-10" [hidden]="!isFiltersOpen">
                            <table-filters [(open)]="isFiltersOpen"
                                           [(ngModel)]="filters"
                                           name="tableFilters"
                                           [filters]="availableFilters"
                                           (onChanges)="updateFilters($event)">
                            </table-filters>
                        </div>
                    </table-container-top>
                    <table-container
                            [pageSizes]="['10', '25', '50', '100']"
                            [collection]="words"
                            (onPageChange)="onPageChange($event)"
                            (onPerPageChange)="onPageSizeChange($event)"
                            [progress]="promise"
                            noResultMessage="{{ 'Good job! No issues were found' | translate }}">
                        <table class="table table-hover">
                            <thead>
                            <tr>
                                <th class="col-sm-1 text-v-middle text-center">
                                    <table-select-item-all class="inline-block" [(selectedItems)]="selected" [items]="words"></table-select-item-all>
                                </th>
                                <th class="col-sm-14 col-md-16 col-lg-20" [attr.aria-label]="'Check all misspellings in this section' | translate">
                                    <table-dictionary-all
                                            class="inline-block"
                                            [selected]="selected"
                                            (callback)="getPage()"
                                            [showConfirm]="type === 'potential'">

                                    </table-dictionary-all>
                                    <span *ngIf="selected.length === 0">
                                        <span monTableSort
                                              monSortBy="word"
                                              [monSortDirection]="currentSort.direction"
                                              [monCurrentlySortedBy]="currentSort.by"
                                              (monOnSorted)="onMonTableSort($event)">
                                            {{ 'Word' | translate }}
                                        </span>
                                    </span>
                                </th>
                                <th class="text-right" *ngIf="showAutofix" translate>PageCorrect</th>
                                <th class="text-right" translate>Lookup in Google</th>
                                <th class="text-right col-sm-5 col-md-5 col-lg-4" translate>Language</th>
                                <th class="col-sm-8 col-md-7 col-lg-5 text-right" *ngIf="type === 'potential'">Confirm</th>
                                <th class="col-sm-8 col-md-7 col-lg-5 text-right"
                                    monTableSort
                                    monSortBy="html_pages_count"
                                    [monSortDirection]="currentSort.direction"
                                    [monCurrentlySortedBy]="currentSort.by"
                                    (monOnSorted)="onMonTableSort($event)">
                                    {{ 'Pages' | translate }}
                                </th>
                                <th class="text-right col-sm-5 col-md-5 col-lg-5"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr *ngFor="let word of words">
                                <td class="text-v-middle text-center" [attr.aria-label]="'Check misspelling' | translate">
                                    <table-select-item class="inline-block" [(selectedItems)]="selected" [item]="word"></table-select-item>
                                </td>
                                <td class="text-v-middle">
                                    {{ word.word }}
                                    <label-table [assignedLabels]="word.labels"
                                                 (getLabels)="getLabels()"
                                                 [attr.aria-label]="'Labels' | translate "
                                                 [existingLabels]="labels"
                                                 (onUpdateLabel)="updateWordsLabel(word)">
                                    </label-table>
                                </td>
                                <td class="text-right text-v-middle" *ngIf="showAutofix && word">
                                    <page-fix-icon (cb)="getPage()" type="{{word.getPageFixType()}}" [model]="word"></page-fix-icon>
                                </td>
                                <td class="text-v-middle text-right">
                                    <a class="btn cursor-pointer" monEventTrigger="actionTrigger" [monEventParams]="{action: 'look-at-google-btn'}" target="_blank" aria-describedby="linkOpensInNewTab" [attr.href]="'https://www.google.com/search?q=' + word.word">
                                        <span class="fab fa-google" aria-hidden="true"></span>
                                        <span class="sr-only" translate>Google</span>
                                    </a>
                                </td>
                                <td class="text-v-middle text-right"> <language language="{{ word.language}}"></language></td>
                                <td class="text-v-middle text-right" *ngIf="type === 'potential'">
                                    <a [attr.aria-label]="'Confirm this word as a misspelling' | translate"
                                       class="cursor-pointer"
                                       [monModalConfirm]="'Are you sure you want to confirm the misspelling <strong>' + word.word + '</strong>?' | translate"
                                       (monModalConfirmAction)="confirmMisspelling(word)">
                                        <span translate>Confirm misspelling</span>
                                    </a>
                                </td>
                                <td class="text-right text-v-middle">
                                    <pages-button (callback)="showPages(word)"
                                                  [ngModel]="word.html_pages_count"
                                                  monAriaLabel="{{'Show pages with this misspelling' | translate}}" />
                                </td>
                                <td class="text-right text-v-middle">
                                    <table-dictionary (callback)="getPage()"
                                                      classification="{{word.classification}}"
                                                      word="{{word.word}}"
                                                      wordId="{{word.id}}"
                                                      language="{{word.language}}"
                                    >
                                    </table-dictionary>
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr *ngIf="selected.length > 0">
                                <td colspan="1" class="text-v-middle text-center" >
                                    <table-select-item-all class="inline-block"
                                                           [(selectedItems)]="selected"
                                                           [items]="words">
                                    </table-select-item-all>
                                </td>
                                <td colspan="6" [attr.aria-label]="'Check all misspellings in this section' | translate">
                                    <table-dictionary-all class="inline-block"
                                                          [selected]="selected"
                                                          (callback)="getPage()"
                                                          [showConfirm]="type === 'potential'">
                                    </table-dictionary-all>
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                    </table-container>
                </div>
            </div>
        </div>

    `,
})
export class SpellingErrorsIndexComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    @Input() type: string = 'confirmed';
    @Input() headline?: string;
    @Input() pageDetailTarget?: string;

    selected: any[] = [];
    domainId: any;
    override search: any = null;
    words: any[] = [];
    tabs: any[] = [];
    override activeTab: any;
    showPlay: boolean = false;
    promise: any = null;
    showAutofix: boolean = false;
    override currentSort: any = { by: 'html_pages_count', direction: 'desc' };
    subheaderTranslation: string = '';
    override filters: any = { labels: [] };
    override availableFilters: any = {
        labels: [],
    };
    override isFiltersOpen: boolean = false;
    override labels: any[] = [];
    override selectedLabels: any[] = [];
    reloadMisspellingEventId: any;

    constructor (
        private translateService: TranslateService,
        private stateService: StateService,
        private monDialog: MonDialog,
        private activeFeatureService: ActiveFeatureService,
        private monEventService: MonEventService,
    ) {
        super({ table: true });
    }

    override getPage = (): void => {
        this.selected = [];
        const params: any = {
            page: this.page,
            page_size: this.pageSize,
            language: this.activeTab,
            search: this.search,
            domainId: this.domainId,
            classification: this.type,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
            page_fixed: false,
        };

        if (this.selectedLabels && this.selectedLabels.length > 0) {
            params['labels[]'] = this.selectedLabels;
        }

        this.promise = this.spellingRepoService.getAllErrors(params).then((data: any) => {
            this.words = data;
            this.showPlay = data.length > 0;
            this.getSubHeaderDescription();
        }, () => {});
    };

    ngOnInit (): void {
        this.selected = [];
        this.type = this.type || 'confirmed';
        this.domainId = this.stateService.params.domainId;
        this.search = null;
        this.headline = this.headline || this.translateService.getString('Misspellings');
        this.words = [];
        this.tabs = [{ name: this.translateService.getString('All'), faIcon: 'fas fa-language', value: null }];
        this.activeTab = this.tabs[0].value;
        this.showPlay = false;
        this.promise = null;
        this.showAutofix = this.activeFeatureService.isFeatureActive('page_fix_qa_misspelling') && this.type !== 'potential';
        this.currentSort = { by: 'html_pages_count', direction: 'desc' };
        this.subheaderTranslation = '';
        this.filters = { labels: [] };
        this.availableFilters = {
            labels: [],
        };
        this.isFiltersOpen = false;
        this.getAllLanguages();
        this.getPage();
        this.getLabels();
        this.reloadMisspellingEventId = this.monEventService.addListener('reloadMisspelling', () => {
            this.getPage();
        });
    }

    ngOnDestroy (): void {
        this.monEventService.remove(this.reloadMisspellingEventId);
    }

    trackByWordId (index: number, word: any): any {
        return word.id;
    }

    confirmMisspelling (word: any): void {
        this.spellingRepoService.confirmWord(word.id, { id: word.id, labels: [] }).then(() => {
            this.getPage();
        }, () => {});
    }

    showPages (word: any): void {
        const params = {
            body: SpellingPagesDialogComponent,
            size: 'lg',
            data: {
                word: word,
                pageDetailTarget: this.pageDetailTarget,
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback: this.getPage });
    }

    playMisspelling (): void {
        this.monDialog.create({
            body: SpellingPlayComponent,
            classes: 'center middle fade-animation',
            size: 'sm',
            data: {
                words: this.words,
                language: this.activeTab,
                search: this.search,
                type: this.type,
            },
        }, () => {
            this.getPage();
        }, {});
    }

    getSubHeaderDescription (): void {
        this.subheaderTranslation = this.translateService.getPlural(
            this.pipeService.get('monNumber')((this.words as any).total, '0,0'),
            'One word found',
            '[[$count]] words found',
            {},
            (this.words as any).total,
        );
    }

    getAllLanguages (): void {
        this.spellingRepoService.getAllLanguages({ domain_id: this.domainId }).then((data: any) => {
            data.forEach((item: any) => {
                let languageCount = item.potential_misspellings_count;
                if (this.type === 'confirmed') {
                    languageCount = item.confirmed_misspellings_count;
                }
                this.tabs.push({
                    name: this.translateService.getString('[[languageName]] ([[languageCount]])', {
                        languageName: this.pipeService.get('translateLanguage')(item.lang),
                        languageCount: this.pipeService.get('monNumber')(languageCount),
                    }),
                    faIcon: 'fas fa-language',
                    value: item.lang,
                });
            });
        }, () => {});
    }

    updateWordsLabel (word: any): void {
        const params: any = {
            id: word.id,
            labels: word.labels.map((label: any) => {
                return label.id;
            }),
        };

        if (this.headline === 'Potential misspellings') {
            params.classification = 'potential';
        }

        this.spellingRepoService.confirmWord(params.id, params).then(() => {}, () => {});
    }

    override updateFilters (changes: any): void {
        if (!changes) {
            return;
        }
        this.selectedLabels = changes[this.translateService.getString('labels')].map((label: any) => {
            return label.id;
        });

        this.getPage();
    }
}
